import React, { useState, useMemo, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Grid,
  Paper,
  Text,
  Title,
  Group,
  Stack,
  TextInput,
  Select,
  Button,
  Badge,
  ActionIcon,
  Box,
  Divider,
  Modal,
  NumberInput,
  Tooltip,
  FileButton,
  Menu,
} from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import {
  IconSearch,
  IconPlus,
  IconFileSpreadsheet,
  IconEdit,
  IconTrash,
  IconDeviceLaptop,
  IconX,
  IconCheck,
  IconSettings,
  IconCoins,
  IconFileUpload,
  IconFileDownload,
  IconTrendingUp,
  IconGift,
} from "@tabler/icons-react";
import {
  MantineReactTable,
  useMantineReactTable,
  MRT_ColumnDef,
  MRT_RowSelectionState,
} from "mantine-react-table";
import { styleTableMantine } from "../../../_base/_const/_constVar";
import { _localization } from "../../../config/location";
import { NotificationExtension } from "../../../_base/extension/NotificationExtension";
import BreadCrumb from "../../../_base/component/_layout/_breadcrumb";
import { modals } from "@mantine/modals";
import FormChooseItemTradeIn from "../TradeInProduct/FormChooseItemTradeIn";
import * as xlsx from "xlsx";
import Repository from "../../../_base/helper/HttpHelper";
import { API_ROUTE } from "../../../const/apiRoute";
import { uploadFile } from "../../../api/ApiProduct";
import { MessageResponse } from "../../../model/MessageResponse";
import { getListCategoryTradeIn } from "../../../api/ApiTradeIn";
import { mapCategoryResponseToState } from "../TradeInCategory";

// Interfaces
interface TradeCategory {
  id: string | number;
  name: string;
  productCount: number;
  status: "active" | "paused";
  type?: string; // "THU MUA" | "LÊN ĐỜI"
  categoryType?: "buyback" | "upgrade";
  headers?: any[];
}

interface TradeProduct {
  id: number;
  catId: number;
  itemId?: number | null;
  productName: string;
  productSku: string;
  productImage?: string | null;
  basePrice: number;
  promoPrice?: number | null;
  customPrice?: number | null;
  supportAmount?: number | null;
  active: "Y" | "N";
  url?: string | null;
  attribute1?: string | null;
}

const formatToSlug = (val: string): string => {
  return val
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/đ/g, "d")
    .replace(/Đ/g, "d")
    .replace(/[^a-z0-9]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-+/, "")
    .replace(/-+$/, "");
};


export const TradeInUpgradeProductV2 = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width: 768px)");
  const panelHeight = isMobile ? undefined : "calc(100vh - 160px)";
  const tableHeight = isMobile ? undefined : "calc(100vh - 330px)";
  const initialCategoryId = location.state?.categoryId || "";

  // States
  const [categories, setCategories] = useState<TradeCategory[]>([]);
  const [products, setProducts] = useState<TradeProduct[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | number>(initialCategoryId);
  const [searchCategory, setSearchCategory] = useState<string>("");
  const [searchProduct, setSearchProduct] = useState<string>("");
  const [searchProductInput, setSearchProductInput] = useState<string>("");
  const [statusFilter, setStatusFilter] = useState<string>("Tất cả");
  const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({});
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modals & Disclosure
  const [editProductModalOpened, editProductModalHandlers] = useDisclosure(false);
  // Modal Inputs state (Edit)
  const [editingProduct, setEditingProduct] = useState<TradeProduct | null>(null);
  const [editProductName, setEditProductName] = useState("");
  const [editProductSku, setEditProductSku] = useState("");
  const [editProductPrice, setEditProductPrice] = useState<number>(0);
  const [editProductSalePrice, setEditProductSalePrice] = useState<number>(0);
  const [editProductSubsidy, setEditProductSubsidy] = useState<number>(0);
  const [editProductStatus, setEditProductStatus] = useState<"Y" | "N">("Y");
  const [editProductImage, setEditProductImage] = useState("");

  // Load Categories from Backend API
  const fetchCategories = async () => {
    try {
      const response = await getListCategoryTradeIn({
        keySearch: searchCategory,
        skip: 0,
        take: 1000,
      });
      if (response && response.success) {
        let items: any[] = [];
        if (response.data) {
          if (Array.isArray(response.data)) {
            items = response.data;
          } else if (response.data.lists) {
            items = response.data.lists;
          }
        } else if (Array.isArray(response.lists)) {
          items = response.lists;
        }

        const mappedList: TradeCategory[] = items
          .filter((item: any) => {
            const typeUpper = (item.type || "").trim().toUpperCase();
            return typeUpper !== "THU MUA";
          })
          .map((item: any) => {
            const stateCat = mapCategoryResponseToState(item);
            return {
              id: stateCat.id,
              name: stateCat.groupName,
              productCount: item.itemCount !== undefined && item.itemCount !== null ? item.itemCount : (item.quantity || 0),
              status: stateCat.status === "1" ? "active" : "paused",
              type: stateCat.type,
              categoryType: stateCat.categoryType,
              headers: stateCat.headers
            };
          });

        setCategories(mappedList);

        if (mappedList.length > 0) {
          const exists = mappedList.some((c) => String(c.id) === String(selectedCategoryId));
          if (!exists) {
            setSelectedCategoryId(mappedList[0].id);
          }
        } else {
          // Debug alert to help identify why the list is empty
          const types = Array.from(new Set(items.map((i: any) => i.type))).filter(Boolean).join(", ");
          NotificationExtension.Warn(
            `Không tìm thấy ngành hàng lên đời! Danh sách từ API có các loại: [${types || "không có dữ liệu loại"}]`
          );
        }
      } else {
        NotificationExtension.Fails("API danh mục trả về success = false hoặc rỗng!");
      }
    } catch (error) {
      console.error("Error fetching categories:", error);
      NotificationExtension.Fails("Không thể tải danh sách ngành hàng!");
    }
  };

  // Load Products from Backend API
  const fetchProducts = async () => {
    if (!selectedCategoryId) return;
    setLoading(true);
    try {
      const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
      let url = `${API_ROUTE.GET_LIST_TRADE_IN_PRODUCT}?Skip=${pagination.pageIndex * pagination.pageSize}&Take=${pagination.pageSize}`;
      if (selectedCategoryId !== "all" && selectedCategoryId !== "") {
        url += `&CatId=${selectedCategoryId}`;
      }
      if (searchProduct) {
        url += `&KeySearch=${encodeURIComponent(searchProduct)}`;
      }
      if (statusFilter !== "Tất cả") {
        url += `&Active=${statusFilter === "Đang hoạt động" ? "Y" : "N"}`;
      }
      
      const response = await repository.post<MessageResponse<any>>(url);
      if (response && response.success && Array.isArray(response.data)) {
        setProducts(response.data);
        setTotal(response.totalCount || response.data.length);
      } else {
        setProducts([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching trade in products:", error);
      NotificationExtension.Fails("Không thể tải danh sách sản phẩm!");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, [searchCategory]);

  useEffect(() => {
    setPagination((prev) => ({ ...prev, pageIndex: 0 }));
  }, [selectedCategoryId, searchProduct, statusFilter]);

  useEffect(() => {
    fetchProducts();
  }, [selectedCategoryId, searchProduct, statusFilter, pagination.pageIndex, pagination.pageSize]);

  // Filter Categories
  const filteredCategories = categories;

  // Active Category Details
  const activeCategory = useMemo(() => {
    return (
      categories.find((c) => String(c.id) === String(selectedCategoryId)) ||
      ({
        id: "",
        name: "",
        productCount: 0,
        status: "active",
        type: "LÊN ĐỜI",
        categoryType: "upgrade",
        headers: [],
      } as TradeCategory)
    );
  }, [categories, selectedCategoryId]);

  // Filtered Products for selected category
  const categoryProducts = products;



  // Mantine React Table Columns
  const columns = useMemo<MRT_ColumnDef<TradeProduct>[]>(
    () => [
      {
        id: "index",
        header: "#",
        size: 50,
        Cell: ({ row }) => row.index + 1,
      },
      {
        accessorKey: "productName",
        header: "Sản phẩm mới",
        size: 240,
        Cell: ({ cell, row }) => (
          <Group gap="xs" wrap="nowrap">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: "4px",
                backgroundColor: "var(--mantine-color-gray-1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                overflow: "hidden",
              }}
            >
              {row.original.productImage ? (
                <img
                  src={row.original.productImage}
                  alt=""
                  style={{ width: "100%", height: "100%", objectFit: "contain" }}
                />
              ) : (
                <IconDeviceLaptop size={20} color="var(--mantine-color-gray-5)" />
              )}
            </Box>
            <Box style={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              <Text size="sm" fw={500} truncate="end">
                {cell.getValue<string>()}
              </Text>
            </Box>
          </Group>
        ),
      },
      {
        accessorKey: "productSku",
        header: "SKU",
        size: 100,
        Cell: ({ cell }) => (
          <Text size="xs" c="dimmed">
            {cell.getValue<string>()}
          </Text>
        ),
      },
      {
        accessorKey: "promoPrice",
        header: "Giá bán",
        size: 120,
        Cell: ({ cell, row }) => {
          const val = cell.getValue<number | null | undefined>();
          const displayPrice = val !== null && val !== undefined ? val : row.original.basePrice;
          return (
            <Text size="sm" fw={600}>
              {displayPrice?.toLocaleString()}đ
            </Text>
          );
        },
      },
      {
        accessorKey: "supportAmount",
        header: "Trợ giá lên đời",
        size: 120,
        Cell: ({ cell }) => {
          const val = cell.getValue<number | null | undefined>();
          return (
            <Text size="sm" fw={700} c="blue">
              {(val || 0).toLocaleString()}đ
            </Text>
          );
        },
      },
      {
        id: "finalUpgradePrice",
        header: "Giá lên đời thực tế",
        size: 140,
        Cell: ({ row }) => {
          const actualPrice = (row.original.promoPrice || row.original.basePrice) - (row.original.supportAmount || 0);
          return (
            <span style={{ color: "var(--mantine-color-teal-7)", fontWeight: 700 }}>
              {actualPrice.toLocaleString()}đ
            </span>
          );
        },
      },
      {
        accessorKey: "active",
        header: "Trạng thái",
        size: 125,
        Cell: ({ cell, row }) => {
          const active = cell.getValue<"Y" | "N" | string>();
          return (
            <Badge
              color={active === "Y" ? "green" : "orange"}
              variant="light"
              style={{ cursor: "pointer" }}
              onClick={() => handleToggleProductStatus(row.original.id, active)}
            >
              {active === "Y" ? "Đang áp dụng" : "Tạm dừng"}
            </Badge>
          );
        },
      },
      {
        id: "actions",
        header: "Thao tác",
        size: 80,
        Cell: ({ row }) => (
          <Group gap={4} justify="center">
            <Tooltip label="Chỉnh sửa sản phẩm" position="top">
              <ActionIcon variant="light" color="orange" onClick={() => handleStartEditProduct(row.original)}>
                <IconEdit size={16} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Xóa" position="top">
              <ActionIcon variant="light" color="red" onClick={() => handleDeleteProduct(row.original.id)}>
                <IconTrash size={16} />
              </ActionIcon>
            </Tooltip>
          </Group>
        ),
        enableSorting: false,
        enableColumnActions: false,
      },
    ],
    []
  );

  const table = useMantineReactTable({
    columns,
    data: categoryProducts,
    enableColumnResizing: false,
    enableSorting: true,
    enableColumnActions: false,
    enableTopToolbar: false,
    positionToolbarAlertBanner: "none",
    enablePagination: true,
    manualPagination: true,
    rowCount: total,
    localization: _localization,
    onPaginationChange: setPagination,
    enableRowSelection: true,
    getRowId: (row) => String(row.id),
    onRowSelectionChange: setRowSelection,
    enableColumnPinning: true,
    initialState: {
      columnPinning: {
        left: ["mrt-row-select"],
        right: ["actions"],
      },
    },
    state: {
      isLoading: loading,
      pagination,
      rowSelection,
    },
    mantineTableContainerProps: {
      style: {
        height: tableHeight,
      },
    },
    mantineTableBodyCellProps: () => ({
      style: {
        ...styleTableMantine,
        padding: "12px 10px",
      },
    }),
    mantineTableHeadCellProps: () => ({
      style: {
        padding: "12px 10px",
      },
    }),
    mantinePaginationProps: {
      showRowsPerPage: true,
      withEdges: true,
      radius: "md",
      size: "md",
      rowsPerPageOptions: ["5", "10", "20", "50"],
    },
    paginationDisplayMode: "pages",
  });

  // Actions
  const handleSelectCategory = (id: string | number) => {
    setSelectedCategoryId(id);
    setSearchProduct("");
    setSearchProductInput("");
    setRowSelection({});
  };

  const handleToggleProductStatus = async (id: number, currentActive: string) => {
    setActionLoading(true);
    try {
      const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
      const dataEditStatus = {
        id: id,
        active: currentActive === "Y" ? "N" : "Y",
      };
      const response = await repository.post<MessageResponse<any>>(
        API_ROUTE.MODIFY_TRADE_IN_PRODUCT_ACTIVE,
        dataEditStatus
      );
      if (response && response.success) {
        NotificationExtension.Success("Cập nhật trạng thái thành công!");
        await fetchProducts();
      }
    } catch (error) {
      console.error("Error toggling status:", error);
      NotificationExtension.Fails("Không thể cập nhật trạng thái!");
    } finally {
      setActionLoading(false);
    }
  };

  const handleDeleteProduct = (id: number) => {
    modals.openConfirmModal({
      title: "Xóa sản phẩm",
      children: (
        <Text size="sm">
          Bạn có chắc chắn muốn xóa sản phẩm này khỏi chương trình trợ giá lên đời?
        </Text>
      ),
      labels: { confirm: "Đồng ý", cancel: "Hủy" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
          const response = await repository.post<MessageResponse<any>>(
            "/TblTradeInProduct/delete",
            id
          );
          if (response && response.success) {
             NotificationExtension.Success("Đã xóa sản phẩm khỏi chương trình trợ giá lên đời!");
             await fetchProducts();
             await fetchCategories();
          }
        } catch (error) {
          console.error("Error deleting product:", error);
          NotificationExtension.Fails("Không thể xóa sản phẩm!");
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  const handleDeleteSelectedProducts = () => {
    const selectedIds = Object.keys(rowSelection).map(Number);
    if (selectedIds.length === 0) return;

    modals.openConfirmModal({
      title: "Xóa nhiều sản phẩm",
      children: (
        <Text size="sm">
          Bạn có chắc chắn muốn xóa {selectedIds.length} sản phẩm đã chọn khỏi chương trình trợ giá lên đời?
        </Text>
      ),
      labels: { confirm: "Đồng ý", cancel: "Hủy" },
      confirmProps: { color: "red" },
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
          const response = await repository.post<MessageResponse<any>>(
            API_ROUTE.DELETES_TRADE_IN_PRODUCT,
            selectedIds
          );
          if (response && response.success) {
            NotificationExtension.Success(`Đã xóa ${selectedIds.length} sản phẩm thành công!`);
            setRowSelection({});
            await fetchProducts();
            await fetchCategories();
          }
        } catch (error) {
          console.error("Error deleting multiple products:", error);
          NotificationExtension.Fails("Không thể xóa các sản phẩm đã chọn!");
        } finally {
          setActionLoading(false);
        }
      }
    });
  };

  // Excel Actions
  const handleExportExcel = () => {
    try {
      const exportData = categoryProducts.map((p, idx) => ({
        "STT": idx + 1,
        "Ngành hàng": categories.find((c) => Number(c.id) === Number(p.catId))?.name || String(p.catId),
        "Tên sản phẩm mới": p.productName,
        "SKU": p.productSku,
        "Giá bán gốc (VNĐ)": p.basePrice,
        "Giá bán lẻ (VNĐ)": p.promoPrice || p.basePrice,
        "Mức trợ giá (VNĐ)": p.supportAmount || 0,
        "Giá lên đời thực tế (VNĐ)": (p.promoPrice || p.basePrice) - (p.supportAmount || 0),
        "Trạng thái": p.active === "Y" ? "Đang hoạt động" : "Tạm dừng",
      }));

      const worksheet = xlsx.utils.json_to_sheet(exportData);
      // Auto-fit columns
      const maxLens = Object.keys(exportData[0] || {}).map((key) => {
        let max = key.length;
        exportData.forEach((row: any) => {
          const val = String(row[key] || "");
          if (val.length > max) max = val.length;
        });
        return { wch: max + 3 };
      });
      worksheet["!cols"] = maxLens;

      const workbook = xlsx.utils.book_new();
      xlsx.utils.book_append_sheet(workbook, worksheet, "Sản phẩm lên đời");
      xlsx.writeFile(workbook, `Danh_Sach_San_Pham_Len_Doi_${selectedCategoryId}.xlsx`);
      NotificationExtension.Success("Xuất Excel thành công!");
    } catch (error) {
      console.error(error);
      NotificationExtension.Fails("Xuất Excel thất bại!");
    }
  };

  const handleDownloadTemplate = async () => {
    setActionLoading(true);
    try {
      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_Demo_APP_API_URL || "";
      const response = await fetch(`${baseUrl}/ExportExcel/export-template-trade-in-product`, {
        method: "GET",
        headers: {
          Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
        },
      });

      if (!response.ok) {
        throw new Error("Không thể tải file mẫu từ máy chủ!");
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = "Mau_Import_Thu_Cu.xlsx";
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      NotificationExtension.Success("Tải file mẫu thành công!");
    } catch (error: any) {
      console.error(error);
      NotificationExtension.Fails(error.message || "Tải file mẫu thất bại!");
    } finally {
      setActionLoading(false);
    }
  };

  const handleImportExcel = async (file: File | null) => {
    if (!file) return;

    const fileType = file.name.split(".").pop()?.toLowerCase();
    if (fileType !== "xlsx" && fileType !== "xls") {
      NotificationExtension.Warn("Vui lòng chọn file Excel (.xlsx, .xls)!");
      return;
    }

    setActionLoading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const token = localStorage.getItem("authToken") || localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_Demo_APP_API_URL || "";
      const response = await fetch(`${baseUrl}/api/v1/ImportExcel/import-trade-in-product`, {
        method: "POST",
        headers: {
          Authorization: token ? `Bearer ${token.replace(/"/g, "")}` : "",
        },
        body: formData,
      });

      const data = await response.json();
      if (response.ok && data.success) {
        NotificationExtension.Success(data.message || "Import dữ liệu thành công!");
        await fetchProducts();
        await fetchCategories();
      } else {
        NotificationExtension.Fails(data.message || "Import dữ liệu thất bại!");
      }
    } catch (error: any) {
      console.error("Import processing error:", error);
      NotificationExtension.Fails("Có lỗi xảy ra khi xử lý file Excel!");
    } finally {
      setActionLoading(false);
    }
  };

  const handleChooseItem = async (selectedItems: any[]) => {
    if (selectedCategoryId === "all" || selectedCategoryId === "") {
      NotificationExtension.Warn("Vui lòng chọn một ngành hàng cụ thể trước khi thêm sản phẩm!");
      return;
    }

    const newItems = selectedItems.filter((item) => !products.some((p) => Number(p.itemId) === Number(item.id)));
    if (newItems.length === 0) {
      NotificationExtension.Warn("Tất cả sản phẩm đã chọn đều đã tồn tại trong ngành hàng này!");
      return;
    }
//1
    setActionLoading(true);
    try {
      const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
      const payload = newItems.map((item) => ({
        catId: Number(selectedCategoryId),
        itemId: item.id,
        productName: item.itemName || "",
        productSku: item.sku || item.itemCode || "",
        productImage: item.primaryImage || null,
        basePrice: item.marketPrice || item.itemCost || 0,
        promoPrice: item.itemCost || item.unitSellingPrice || 0,
        supportAmount: 2000000, // default subsidy
        active: "Y",
        url: item.url || "",
        attribute1: item.url || ""
      }));

      const response = await repository.post<MessageResponse<any>>(
        API_ROUTE.CREATE_TRADE_IN_PRODUCT,
        payload
      );
      //1111
      if (response && response.success) {
        const skippedCount = selectedItems.length - newItems.length;
        if (skippedCount > 0) {
          NotificationExtension.Success(`Đã thêm ${newItems.length} sản phẩm thành công (bỏ qua ${skippedCount} sản phẩm đã tồn tại)!`);
        } else {
          NotificationExtension.Success(`Đã thêm ${newItems.length} sản phẩm thành công!`);
        }
        await fetchProducts();
        await fetchCategories();
      }
    } catch (error) {
      console.error("Error choosing product from DB:", error);
      NotificationExtension.Fails("Không thể thêm sản phẩm!");
    } finally {
      setActionLoading(false);
    }
  };

  const handleOpenFormChooseItem = () => {
    modals.openConfirmModal({
      modalId: "item",
      title: (
        <Group justify="space-between" align="center" style={{ width: "100%" }}>
          <Title order={5}>Chọn sản phẩm mới từ cơ sở dữ liệu</Title>
        </Group>
      ),
      children: <FormChooseItemTradeIn onChooseItem={handleChooseItem} excludeTradeInType={activeCategory.type} />,
      confirmProps: { display: "none" },
      cancelProps: { display: "none" },
      size: "xl",
    });
  };

  const handleStartEditProduct = (prod: TradeProduct) => {
    setEditingProduct(prod);
    setEditProductName(prod.productName);
    setEditProductSku(prod.productSku);
    setEditProductPrice(prod.basePrice);
    setEditProductSalePrice(prod.promoPrice || prod.basePrice);
    setEditProductSubsidy(prod.supportAmount || 0);
    setEditProductStatus(prod.active);
    setEditProductImage(prod.productImage || "");
    editProductModalHandlers.open();
  };

  const handleEditProductSubmit = async () => {
    if (!editProductName.trim() || !editProductSku.trim()) {
      NotificationExtension.Warn("Vui lòng điền đầy đủ tên và SKU!");
      return;
    }
    if (!editingProduct) return;

    setActionLoading(true);
    try {
      const repository = new Repository(process.env.REACT_APP_Demo_APP_API_URL);
      const payload = {
        id: editingProduct.id,
        catId: editingProduct.catId,
        itemId: editingProduct.itemId || null,
        productName: editProductName,
        productSku: editProductSku,
        productImage: editProductImage || null,
        basePrice: editProductPrice,
        promoPrice: editProductSalePrice,
        supportAmount: editProductSubsidy,
        active: editProductStatus,
        url: editingProduct.itemId ? (editingProduct.url || null) : formatToSlug(editProductName),
        attribute1: editingProduct.itemId ? (editingProduct.attribute1 || editingProduct.url || null) : formatToSlug(editProductName)
      };

      const response = await repository.put<MessageResponse<any>>(
        API_ROUTE.MODIFY_TRADE_IN_PRODUCT,
        payload
      );
      if (response && response.success) {
        NotificationExtension.Success("Đã cập nhật thông tin trợ giá sản phẩm thành công!");
        editProductModalHandlers.close();
        setEditingProduct(null);
        setEditProductImage("");
        await fetchProducts();
      }
    } catch (error) {
      console.error("Error updating trade in product:", error);
      NotificationExtension.Fails("Không thể cập nhật sản phẩm!");
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <Box className="trade-in-v2-container" p="md" style={{ width: "100%", boxSizing: "border-box" }}>
      <style>{`
        .trade-in-v2-container ::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        .trade-in-v2-container ::-webkit-scrollbar-track {
          background: transparent;
        }
        .trade-in-v2-container ::-webkit-scrollbar-thumb {
          background: var(--mantine-color-gray-3);
          border-radius: 10px;
        }
        .trade-in-v2-container ::-webkit-scrollbar-thumb:hover {
          background: var(--mantine-color-gray-5);
        }
        .trade-in-v2-container * {
          scrollbar-width: thin;
          scrollbar-color: var(--mantine-color-gray-3) transparent;
        }
      `}</style>
      <Group gap={0} justify="space-between" mb={10}>
        <BreadCrumb />
      </Group>

      <Grid gutter="md">
        {/* Left Column: Categories (Commented out to use Dropdown Select instead) */}
        {/*
        <Grid.Col span={{ base: 12, md: 2, lg: 2 }}>
          <Paper p="xs" withBorder radius="md" style={{ display: "flex", flexDirection: "column", height: panelHeight }}>
            <Title order={6} mb="xs" fw={700}>
              Ngành hàng áp dụng
            </Title>
            <TextInput
              placeholder="Tìm nhanh..."
              leftSection={<IconSearch size={14} />}
              value={searchCategory}
              onChange={(e) => setSearchCategory(e.currentTarget.value)}
              mb="sm"
              size="xs"
            />
            <Stack gap={4} style={{ flex: 1, overflowY: "auto" }}>
              {filteredCategories.map((cat) => {
                const isActive = selectedCategoryId === cat.id;
                return (
                  <Box
                    key={cat.id}
                    onClick={() => handleSelectCategory(cat.id)}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      padding: "8px 10px",
                      borderRadius: "6px",
                      cursor: "pointer",
                      backgroundColor: isActive ? "var(--mantine-color-blue-0)" : "transparent",
                      color: isActive ? "var(--mantine-color-blue-7)" : "inherit",
                      transition: "background-color 0.2s ease",
                    }}
                  >
                    <Text fw={isActive ? 600 : 400} style={{ fontSize: 13 }}>
                      {cat.name}
                    </Text>
                    <Badge variant={isActive ? "filled" : "light"} color={isActive ? "blue" : "gray"} size="xs">
                      {cat.productCount}
                    </Badge>
                  </Box>
                );
              })}
            </Stack>
          </Paper>
        </Grid.Col>
        */}

        {/* Center Column: Table (Expanded to 12) */}
        <Grid.Col span={12}>
          <Paper p="md" withBorder radius="md" style={{ display: "flex", flexDirection: "column", height: panelHeight }}>
            <Group justify="space-between" mb="xs">
              <Title order={5} fw={700}>
                Sản phẩm lên đời:{" "}
                <Text span c="blue" fw={700}>
                  {activeCategory.name}
                </Text>{" "}
                ({categoryProducts.length} sản phẩm)
              </Title>
            </Group>

            <Group justify="space-between" align="center" gap="xs" mb="sm">
              <Group gap={6}>
                <Select
                  placeholder="Chọn ngành hàng"
                  data={categories.map((cat) => ({
                    value: String(cat.id),
                    label: cat.name,
                  }))}
                  value={selectedCategoryId ? String(selectedCategoryId) : null}
                  onChange={(val) => {
                    if (val) handleSelectCategory(val);
                  }}
                  searchable
                  allowDeselect={false}
                  size="xs"
                  style={{ width: 220 }}
                />

                <TextInput
                  placeholder="Tên hoặc SKU sản phẩm..."
                  value={searchProductInput}
                  onChange={(e) => setSearchProductInput(e.currentTarget.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      setSearchProduct(searchProductInput);
                    }
                  }}
                  style={{ width: 190 }}
                  size="xs"
                />

                <Button
                  leftSection={<IconSearch size={14} />}
                  color="blue"
                  onClick={() => setSearchProduct(searchProductInput)}
                  size="xs"
                >
                  Tìm kiếm
                </Button>
              </Group>

              <Group gap={6}>
                {Object.keys(rowSelection).length > 0 && (
                  <Button
                    color="red"
                    variant="filled"
                    size="xs"
                    leftSection={<IconTrash size={15} />}
                    onClick={handleDeleteSelectedProducts}
                  >
                    Xóa ({Object.keys(rowSelection).length})
                  </Button>
                )}
                <Button
                  leftSection={<IconPlus size={15} />}
                  onClick={handleOpenFormChooseItem}
                  size="xs"
                >
                  Chọn sản phẩm mới
                </Button>

                <Button
                  leftSection={<IconFileDownload size={15} />}
                  onClick={handleDownloadTemplate}
                  size="xs"
                  color="green"
                  variant="outline"
                  loading={actionLoading}
                >
                  Tải file mẫu
                </Button>
                <FileButton onChange={handleImportExcel} accept=".xls,.xlsx">
                  {(props) => (
                    <Button
                      {...props}
                      leftSection={<IconFileUpload size={15} />}
                      size="xs"
                      color="teal"
                      variant="outline"
                      loading={actionLoading}
                    >
                      Import Excel
                    </Button>
                  )}
                </FileButton>



              </Group>
            </Group>

            <MantineReactTable table={table} />
          </Paper>
        </Grid.Col>
      </Grid>



      {/* Modal: Edit product target */}
      <Modal opened={editProductModalOpened} onClose={() => { editProductModalHandlers.close(); setEditingProduct(null); }} title="Cấu hình Trợ giá sản phẩm lên đời" centered size="lg">
        <Stack gap="md">
          <TextInput
            label="Tên sản phẩm mới"
            required
            value={editProductName}
            onChange={(e) => setEditProductName(e.currentTarget.value)}
            disabled={editingProduct?.itemId !== null}
          />
          <Grid>
            <Grid.Col span={6}>
              <TextInput
                label="SKU"
                required
                value={editProductSku}
                onChange={(e) => setEditProductSku(e.currentTarget.value)}
                disabled={editingProduct?.itemId !== null}
              />
            </Grid.Col>
            <Grid.Col span={6}>
              <TextInput
                label="Hình ảnh sản phẩm (URL)"
                value={editProductImage}
                onChange={(e) => setEditProductImage(e.currentTarget.value)}
                disabled={editingProduct?.itemId !== null}
                rightSection={
                  (editingProduct?.itemId === null) && (
                    <FileButton
                      onChange={async (file) => {
                        if (file) {
                          const result = await uploadFile([file], "tradein");
                          if (result && result.success && result.data?.[0]?.url) {
                            setEditProductImage(result.data[0].url);
                          }
                        }
                      }}
                      accept="image/*"
                    >
                      {(props) => (
                        <Button {...props} size="xs" variant="subtle" px={5}>
                          Tải lên
                        </Button>
                      )}
                    </FileButton>
                  )
                }
                rightSectionWidth={70}
              />
            </Grid.Col>
          </Grid>
          <Grid>
            <Grid.Col span={4}>
              <NumberInput
                label="Giá bán gốc"
                required
                thousandSeparator="."
                decimalSeparator=","
                value={editProductPrice}
                onChange={(val) => setEditProductPrice(typeof val === "number" ? val : 0)}
                disabled={editingProduct?.itemId !== null}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Giá bán lẻ"
                required
                thousandSeparator="."
                decimalSeparator=","
                value={editProductSalePrice}
                onChange={(val) => setEditProductSalePrice(typeof val === "number" ? val : 0)}
                disabled={editingProduct?.itemId !== null}
              />
            </Grid.Col>
            <Grid.Col span={4}>
              <NumberInput
                label="Số tiền trợ giá"
                required
                thousandSeparator="."
                decimalSeparator=","
                value={editProductSubsidy}
                onChange={(val) => setEditProductSubsidy(typeof val === "number" ? val : 0)}
              />
            </Grid.Col>
          </Grid>
          <Select
            label="Trạng thái hoạt động"
            data={[
              { value: "Y", label: "Đang áp dụng" },
              { value: "N", label: "Tạm dừng" },
            ]}
            value={editProductStatus}
            onChange={(val) => setEditProductStatus((val || "Y") as any)}
          />
          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => { editProductModalHandlers.close(); setEditingProduct(null); }}>
              Hủy
            </Button>
            <Button onClick={handleEditProductSubmit}>Lưu cấu hình</Button>
          </Group>
        </Stack>
      </Modal>
    </Box>
  );
};
